// frontend/shared/src/time.ts —— D8-12 / TD-815：apitime（RFC 3339 UTC）解析，按 timezone 渲染
const cache = new Map<string, Intl.DateTimeFormat>();

type Part = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second';

function fmt(tz: string | undefined): Intl.DateTimeFormat {
  const key = tz ?? '';
  let f = cache.get(key);
  if (!f) {
    // 非法 IANA 名会抛 RangeError，由调用方 safeParts 兜底
    f = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
    cache.set(key, f);
  }
  return f;
}

function isPart(t: string): t is Part {
  return t === 'year' || t === 'month' || t === 'day' || t === 'hour' || t === 'minute' || t === 'second';
}

function parts(d: Date, tz: string | undefined): Record<Part, string> {
  const out: Record<Part, string> = { year: '', month: '', day: '', hour: '', minute: '', second: '' };
  for (const p of fmt(tz).formatToParts(d)) if (isPart(p.type)) out[p.type] = p.value;
  return out;
}

// 配置下发的 timezone 非法时退回设备本地时区，避免整页渲染抛错（只告警一次 / 每个 tz）
const badTz = new Set<string>();
function safeParts(d: Date, tz: string | undefined): Record<Part, string> {
  if (tz === undefined) return parts(d, undefined);
  try {
    return parts(d, tz);
  } catch (e) {
    if (!badTz.has(tz)) {
      badTz.add(tz);
      console.error('[gp:time] 非法时区，退回本地时区', tz, e);
    }
    return parts(d, undefined);
  }
}

function parse(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

// tz 省略 = 设备本地时区（H5）；代理 / 后台传 config.timezone
export function fmtDateTime(iso: string | null | undefined, o: { tz?: string; seconds?: boolean } = {}): string {
  const d = iso ? parse(iso) : null;
  if (!d) return '—';
  const p = safeParts(d, o.tz);
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}${o.seconds ? ':' + p.second : ''}`;
}

export function fmtDate(iso: string | null | undefined, tz?: string): string {
  const d = iso ? parse(iso) : null;
  if (!d) return '—';
  const p = safeParts(d, tz);
  return `${p.year}-${p.month}-${p.day}`;
}

export function fmtMonthDay(iso: string | null | undefined, tz?: string): string {
  const d = iso ? parse(iso) : null;
  if (!d) return '—';
  const p = safeParts(d, tz);
  return `${Number(p.month)}/${Number(p.day)}`;
}

export function todayIn(tz: string | undefined, now: Date = new Date()): string {
  const p = safeParts(now, tz);
  return `${p.year}-${p.month}-${p.day}`;
}

export function tzOffsetLabel(tz: string, at: Date = new Date()): string {
  const p = safeParts(at, tz);
  const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
  const mins = Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000);
  const sign = mins < 0 ? '-' : '+';
  const a = Math.abs(mins);
  return `UTC${sign}${Math.floor(a / 60)}${a % 60 ? ':' + String(a % 60).padStart(2, '0') : ''}`;
}
